import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import {
	formatSecondsToDurationLabel,
	resolveMasteryColorForPercentage,
} from "@memorang/helpers/common";
import { isAnswerSkipped } from "@memorang/helpers/item";
import type {
	AIFeedbackResponse,
	Answer,
	BaseItemSchema,
} from "@memorang/types";
import type { Stat } from "@memorang/ui/components/RowStats";
import type { SubSkillItem } from "@memorang/ui/components/StudySession/AiPostAnswerHeader";
import glyphMap from "@react-native-vector-icons/material-design-icons/glyphmaps/MaterialDesignIcons.json";
import type { TFunction } from "i18next";
import {
	getPointsEarned,
	resolveSubskillAssessments,
} from "../../helpers/answer-score";
import { isMultiSelect as isMultiSelectItem } from "../../helpers/item-type-guards";

const scorePerAttempt = [10, 5, 3, 2, 1];

export const calculateXp = (numAttempts: number, isHintShown: boolean) => {
	const score = scorePerAttempt[numAttempts - 1] || 0;
	return isHintShown ? score : score * 2;
};

type PostAnswerTheme = {
	dark: boolean;
	colors: {
		error: string;
		onSurfaceVariant: string;
		text: string;
	};
	palette: {
		confidence: {
			high: string;
			low: string;
			medium: string;
		};
		progress: {
			high: string;
			low: string;
			medium: string;
			none: string;
		};
		success: {
			main: string;
			dark: string;
		};
		yellow: {
			light: string;
		};
	};
};

export type PostAnswerStat = Stat;

export type PostAnswerSubSkillItem = SubSkillItem;

const isHighConfidence = (confidence: number | undefined) =>
	confidence === 100 || confidence === 2;

const isMediumConfidence = (confidence: number | undefined) =>
	confidence === 50 || confidence === 1;

const isLowConfidence = (confidence: number | undefined) => confidence === 0;

const getConfidenceLabel = (confidence: number | undefined, t: TFunction) => {
	if (isHighConfidence(confidence)) {
		return t("session.confidence.high");
	}
	if (isMediumConfidence(confidence)) {
		return t("session.confidence.medium");
	}
	if (isLowConfidence(confidence)) {
		return t("session.confidence.low");
	}
	return undefined;
};

export type BuildPostAnswerStatsParams = {
	aiScore?: number;
	attemptsStatValue: string;
	confidence?: number;
	correct?: boolean;
	currentAnswer?: Answer;
	currentItem: BaseItemSchema;
	currentUserResponse?: string;
	isAssessorItemType: boolean;
	maximumScore: number;
	numResponses?: number;
	numberOfWords?: number;
	recordingDurationSeconds?: number;
	showNumAttempts?: boolean;
	showXpEarned?: boolean;
	t: TFunction;
	theme: PostAnswerTheme;
	timeTaken: number;
	xpEarned: number;
};

type StatHelperContext = { t: TFunction; theme: PostAnswerTheme };

const buildSkillStat = (
	currentItem: BaseItemSchema,
	skill: string,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => ({
	icon:
		currentItem.response.type === "recording"
			? "microphone-outline"
			: "pencil-outline",
	label: t("session.stats.skill"),
	value: skill,
	color: theme.colors.onSurfaceVariant,
	tooltip: t("session.stats.skill_tooltip", { skill }),
});

const buildBlanksScoreStat = (
	pointsEarned: number,
	maximumScore: number,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => ({
	icon: "format-list-checks",
	label: t("session.stats.answered"),
	value: `${pointsEarned}/${maximumScore}`,
	color:
		pointsEarned === maximumScore
			? theme.palette.success.main
			: theme.colors.error,
	tooltip: t("session.stats.blanks_score", {
		earned: pointsEarned,
		total: maximumScore,
		defaultValue: "{{earned}} of {{total}} correct",
	}),
});

const buildAnsweredStat = (
	correct: boolean | undefined,
	skipped: boolean,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => {
	const isCorrect = !skipped && correct;
	return {
		icon: skipped
			? "minus-circle-outline"
			: isCorrect
				? "thumb-up-outline"
				: "thumb-down-outline",
		label: skipped ? t("session.answer.skipped") : t("session.stats.answered"),
		value: isCorrect
			? t("session.answer.correct")
			: t("session.answer.incorrect"),
		color: isCorrect ? theme.palette.success.main : theme.colors.error,
		tooltip: skipped
			? t("session.stats.skipped_tooltip")
			: isCorrect
				? t("session.stats.answered_correct_tooltip")
				: t("session.stats.answered_incorrect_tooltip"),
	};
};

const buildWordCountStat = (
	numberOfWords: number,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => ({
	icon: "text-box-check-outline",
	label:
		numberOfWords === 1 ? t("session.stats.word") : t("session.stats.words"),
	value: numberOfWords.toString(),
	color: theme.colors.onSurfaceVariant,
	tooltip:
		numberOfWords === 1
			? t("session.stats.word_tooltip", { count: numberOfWords })
			: t("session.stats.words_tooltip", { count: numberOfWords }),
});

const buildTimeStat = (
	timeTaken: number,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => ({
	icon: "timer-outline",
	label: t("session.stats.speed"),
	value: formatSecondsToDurationLabel(timeTaken, true),
	color: theme.colors.onSurfaceVariant,
	tooltip: t("session.stats.time_taken_tooltip", { time_taken: timeTaken }),
});

const buildDurationStat = (
	durationSeconds: number,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => ({
	icon: "waveform",
	label: t("session.stats.duration"),
	value: t("session.stats.time_taken", {
		time: durationSeconds,
		sec: t("common.sec", { count: durationSeconds }),
	}),
	color: theme.colors.onSurfaceVariant,
	tooltip: t("session.stats.duration_tooltip", { duration: durationSeconds }),
});

const buildXpStat = (
	xpEarned: number,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => ({
	icon: "star-four-points-outline",
	label: "PTS EARNED",
	value: `${xpEarned} XP`,
	color: theme.palette.yellow.light,
	tooltip: t("session.stats.pts_earned_tooltip", { xp: xpEarned }),
});

const buildConfidenceStat = (
	confidence: number,
	confidenceLabel: string,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => ({
	icon: isHighConfidence(confidence)
		? "emoticon-happy-outline"
		: isLowConfidence(confidence)
			? "emoticon-sad-outline"
			: "emoticon-neutral-outline",
	label: t("session.stats.confidence"),
	value: confidenceLabel,
	color: isHighConfidence(confidence)
		? theme.palette.confidence.high
		: isLowConfidence(confidence)
			? theme.palette.confidence.low
			: theme.palette.confidence.medium,
	tooltip: t("session.stats.confidence_tooltip"),
});

const buildPointsEarnedStat = (
	pointsEarned: number,
	maximumScore: number,
	{ t, theme }: StatHelperContext,
): PostAnswerStat => {
	const roundedPoints = Math.round(pointsEarned);
	return {
		icon: "star-outline",
		label: t("session.stats.pointsEarned"),
		value: `${roundedPoints} / ${maximumScore}`,
		color: theme.colors.onSurfaceVariant,
		tooltip: t("session.stats.points_earned_tooltip", {
			count: roundedPoints,
			points: roundedPoints,
			maximumScore,
		}),
	};
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
export function buildPostAnswerStats({
	aiScore,
	attemptsStatValue,
	confidence,
	correct,
	currentAnswer,
	currentItem,
	currentUserResponse,
	isAssessorItemType,
	maximumScore,
	numResponses,
	numberOfWords,
	recordingDurationSeconds,
	showNumAttempts,
	showXpEarned,
	t,
	theme,
	timeTaken,
	xpEarned,
}: BuildPostAnswerStatsParams): PostAnswerStat[] {
	const ctx: StatHelperContext = { t, theme };
	const skipped = isAnswerSkipped(currentAnswer, currentUserResponse);
	const isWriting = currentItem.response.type === "writing";
	const isRecording = currentItem.response.type === "recording";
	const skill = isRecording ? "Speaking" : isWriting ? "Writing" : null;
	const pointsEarned = getPointsEarned({
		aiScore,
		correct,
		currentAnswer,
		isAssessorItemType,
		maximumScore,
	});
	const confidenceLabel =
		confidence != null ? getConfidenceLabel(confidence, t) : undefined;
	const isAnswerKeyMapping =
		currentItem.scoring != null &&
		"type" in currentItem.scoring &&
		currentItem.scoring.type === "ANSWER_KEY_MAPPING";

	const isMultiSelect = isMultiSelectItem(currentItem);

	if (showNumAttempts && attemptsStatValue) {
		return [
			{
				icon: "sword-cross",
				label: "Challengers",
				value: String(numResponses ?? 0),
				color: theme.palette.success.dark,
				tooltip: t("session.stats.challengers_tooltip", {
					count: numResponses ?? 0,
				}),
			},
			{
				icon: "star-outline",
				label: "Points",
				value: `+${xpEarned} XP`,
				color: theme.palette.yellow.light,
				tooltip: t("session.stats.learning_mode_points_tooltip", {
					xp: xpEarned,
				}),
			},
		];
	}

	const stats: PostAnswerStat[] = [];

	if (skill) {
		stats.push(buildSkillStat(currentItem, skill, ctx));
	}

	if (!isAssessorItemType) {
		if (isAnswerKeyMapping) {
			stats.push(buildBlanksScoreStat(pointsEarned, maximumScore, ctx));
		} else if (!isMultiSelect) {
			stats.push(buildAnsweredStat(correct, skipped ?? false, ctx));
		}
	}

	if (isWriting && numberOfWords != null) {
		stats.push(buildWordCountStat(numberOfWords, ctx));
	} else if (!showXpEarned) {
		stats.push(buildTimeStat(timeTaken, ctx));
	}

	// Hidden when unknown (0) — web webm audio resumed from a URL reports no duration.
	if (isRecording && recordingDurationSeconds) {
		stats.push(buildDurationStat(recordingDurationSeconds, ctx));
	}

	if (showXpEarned) {
		stats.push(buildXpStat(xpEarned, ctx));
	}

	if (confidence != null && !isAssessorItemType && confidenceLabel != null) {
		stats.push(buildConfidenceStat(confidence, confidenceLabel, ctx));
	}

	if (maximumScore && !isAssessorItemType) {
		stats.push(buildPointsEarnedStat(pointsEarned, maximumScore, ctx));
	}

	return stats;
}

export type BuildSubskillItemsParams = {
	aiResponse?: AIFeedbackResponse;
	currentItem: BaseItemSchema;
	masteryLevels?: AppConfigMasteryLevelsType;
	maximumScore: number;
	t: TFunction;
	theme: PostAnswerTheme;
};

const LUCIDE_TO_MCI: Record<string, string> = {
	"message-square-text": "message-text-outline",
	"file-text": "file-document-outline",
	"spell-check": "spellcheck",
	"spell-check-2": "spellcheck",
	"book-open-text": "book-open-page-variant",
	workflow: "view-list-outline",
	"audio-lines": "waveform",
	gauge: "gauge",
	mic: "microphone",
	"volume-2": "volume-high",
	activity: "pulse",
	waves: "waveform",
};

const progressBandColor = (percentage: number, theme: PostAnswerTheme) =>
	percentage < 40
		? theme.palette.progress.low
		: percentage < 70
			? theme.palette.progress.medium
			: theme.palette.progress.high;

export function buildSubskillItems({
	aiResponse,
	currentItem,
	masteryLevels,
	maximumScore,
	t,
	theme,
}: BuildSubskillItemsParams): PostAnswerSubSkillItem[] {
	const criteriaEntries = currentItem.scoring?.criteria
		? Object.entries(currentItem.scoring.criteria).filter(
				([, criterion]) => criterion != null,
			)
		: [];
	const assessments = resolveSubskillAssessments({
		item: currentItem,
		aiResponse,
	});
	const subskillByKey = new Map(
		assessments
			.filter((subskill) => subskill.key != null)
			.map((subskill) => [subskill.key, subskill]),
	);
	const subskillByName = new Map(
		assessments.map((subskill) => [subskill.name?.toLowerCase(), subskill]),
	);

	return criteriaEntries.map(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: maps scoring criteria into UI display rows.
		([criterionKey, criterion]) => {
			const { name, icon, description, maxPoints, points } = criterion;
			const label = name.startsWith("session.")
				? t(`${name}.title`, { defaultValue: name })
				: name;
			const subskill =
				subskillByKey.get(criterionKey) ??
				subskillByName.get(label.toLowerCase());
			const score = subskill?.score;
			const displayScore =
				subskill?.displayScore ?? (score != null ? Math.round(score) : null);
			const mappedIcon = icon ? (LUCIDE_TO_MCI[icon] ?? icon) : "star-outline";
			const displayIcon = (
				mappedIcon in glyphMap ? mappedIcon : "star-outline"
			) as SubSkillItem["icon"];
			const rowMax = maxPoints || points?.max || maximumScore;
			const normalizedScore =
				displayScore == null ? undefined : (displayScore / rowMax) * 100;
			const backgroundColor =
				normalizedScore === undefined
					? theme.palette.progress.none
					: (resolveMasteryColorForPercentage(
							normalizedScore,
							masteryLevels,
							theme.dark,
						) ?? progressBandColor(normalizedScore, theme));

			return {
				label,
				icon: displayIcon,
				description: description ?? "",
				maxPoints: rowMax,
				pending: score === undefined,
				scoreText: `${displayScore ?? "--"} / ${rowMax}`,
				chipProps: {
					backgroundColor,
					color: normalizedScore === undefined ? theme.colors.text : "white",
				},
			};
		},
	);
}
